/**
 * The player's DISPLAY currency — the second half of `PATCH /me/preferences`.
 *
 * `engine/i18n/runtime.ts` owns the `locale` half of that endpoint. This module
 * owns `currency`, in the same shape and with the same rules, because the two
 * are one row on `users` and one PATCH body: keeping them in separate modules
 * with matching APIs is cheaper than one module that half-belongs to i18n.
 *
 * ── WHAT THIS IS AND IS NOT ─────────────────────────────────────────────────
 * It is a PRESENTATION preference. Every internal balance, ledger row and
 * invoice on the backend is EUR minor units, and `core/exchange-rate` converts
 * for display only. Choosing GBP here does not change what a player is charged;
 * `settings.currency_help` says exactly that, and any screen showing a price
 * must keep saying it. Nothing in this file converts anything.
 *
 * ── AND THE SHOP DOES NOT CONVERT EITHER ────────────────────────────────────
 * An earlier version of this header said conversion was "the shop's job,
 * against `GET /rates`, when a shop exists". A shop now exists and that is not
 * what it does. `backend/src/game/shop/packs.data.ts` authors a SEPARATE PRICE
 * PER CURRENCY per rung and `packs.service.ts` READS the column verbatim —
 * `priceMinor = pack.prices[currency]`, no rate, no rounding, not even as a
 * fallback. A rate-derived price drifts from what the provider actually
 * charges between the screenshot and the card. See `PACK_CURRENCIES` at the
 * foot of this file.
 *
 * ── THE LIST IS THE SERVER'S, NOT OURS ──────────────────────────────────────
 * `GET /i18n/config` reports the currencies this deployment enables (a subset
 * of EUR/GBP/USD, set by the `CURRENCIES` env var) and its default. We ask,
 * rather than hard-coding three codes, so a deployment that enables only EUR
 * shows an indicator instead of a pointless one-option select — and so a
 * fourth currency turns up in the footer the day operations enables it,
 * without a frontend deploy. `FALLBACK_CURRENCIES` is used only when that call
 * fails, and it is the same list core compiles in.
 */
import { api } from '../../core/network/api';
import { authStore } from '../../core/state/auth-store';

/** What core's `SUPPORTED_CURRENCIES` contains today. Used only if `/i18n/config` fails. */
export const FALLBACK_CURRENCIES: readonly string[] = Object.freeze(['EUR', 'GBP', 'USD']);

/** Core's `DEFAULT_CURRENCY`, and the currency every internal amount is stored in. */
export const BASE_CURRENCY = 'EUR';

/** `localStorage` key for the device-local choice. Namespaced like `rga.locale`. */
export const CURRENCY_STORAGE_KEY = 'rga.currency';

/** One selectable currency, as `/i18n/config` describes it. */
export interface CurrencyOption {
  readonly code: string;
  /** Localised symbol from the server (`€`, `£`, `$`). May be the code itself. */
  readonly symbol: string;
}

export interface CurrencySnapshot {
  readonly currency: string;
  readonly options: readonly CurrencyOption[];
  /** Where the active value came from — mirrors i18n's `LocaleSource`. */
  readonly source: CurrencySource;
}

export type CurrencySource = 'account' | 'storage' | 'server-default' | 'fallback';

/** Minimal `localStorage` shape, so a test can pass a Map-backed stub. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SetCurrencyOptions {
  /** Remember on this device. Default true. */
  persist?: boolean;
  /** Push to the account when signed in. Default true. */
  remote?: boolean;
}

export interface InitCurrencyOptions {
  /** Force a value, skipping resolution (tests). */
  currency?: string;
  /** `null` disables persistence; omitted probes `localStorage`. */
  storage?: StorageLike | null;
  loadConfig?: () => Promise<CurrencyConfig | null>;
  loadAccountCurrency?: () => Promise<string | null>;
  savePreference?: (currency: string) => Promise<void>;
  isAuthenticated?: () => boolean;
}

/** The slice of `GET /i18n/config` this module reads. */
export interface CurrencyConfig {
  readonly currencies: readonly CurrencyOption[];
  readonly defaultCurrency: string;
}

/* ═══════════════════════════════════════════════ injectable services ════ */

interface Deps {
  storage: StorageLike | null;
  loadConfig(): Promise<CurrencyConfig | null>;
  loadAccountCurrency(): Promise<string | null>;
  savePreference(currency: string): Promise<void>;
  isAuthenticated(): boolean;
}

/** `localStorage` throws outright in a locked-down browser — probe, never assume. */
function usableStorage(): StorageLike | null {
  try {
    const probe = globalThis.localStorage;
    if (probe === undefined || probe === null) return null;
    const key = '__rga_currency_probe__';
    probe.setItem(key, '1');
    probe.removeItem(key);
    return probe;
  } catch {
    return null;
  }
}

let configCache: CurrencyConfig | null = null;

const defaultLoadConfig = async (): Promise<CurrencyConfig | null> => {
  if (configCache !== null) return configCache;
  try {
    const raw = await api.get<{
      currencies?: unknown;
      defaultCurrency?: unknown;
    }>('/i18n/config');
    const parsed = parseCurrencyConfig(raw);
    if (parsed !== null) configCache = parsed;
    return parsed;
  } catch {
    return null;
  }
};

const defaultLoadAccountCurrency = async (): Promise<string | null> => {
  try {
    const prefs = await api.get<{ currency: string | null }>('/me/preferences');
    return typeof prefs?.currency === 'string' ? prefs.currency : null;
  } catch {
    return null;
  }
};

const defaultSavePreference = async (currency: string): Promise<void> => {
  await api.patch('/me/preferences', { currency });
};

function freshDeps(): Deps {
  return {
    storage: usableStorage(),
    loadConfig: defaultLoadConfig,
    loadAccountCurrency: defaultLoadAccountCurrency,
    savePreference: defaultSavePreference,
    isAuthenticated: () => authStore.get().user !== null,
  };
}

let deps: Deps = freshDeps();

/* ═══════════════════════════════════════════════════════════ state ══════ */

let options: readonly CurrencyOption[] = FALLBACK_CURRENCIES.map((code) => ({
  code,
  symbol: code,
}));
let active = BASE_CURRENCY;
let source: CurrencySource = 'fallback';
const listeners = new Set<(snapshot: CurrencySnapshot) => void>();

/**
 * Normalise a candidate code. Currencies are ISO-4217: three ASCII letters,
 * upper case. Anything else — a locale that leaked into the wrong field, an
 * empty string, `"eur "` — is not a currency and is rejected here rather than
 * being sent to a server that will 400 on it.
 */
export function normaliseCurrencyCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** Parse `/i18n/config` defensively; `null` when it is not the shape we need. */
export function parseCurrencyConfig(raw: unknown): CurrencyConfig | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.currencies)) return null;

  const parsed: CurrencyOption[] = [];
  for (const entry of record.currencies) {
    if (typeof entry !== 'object' || entry === null) continue;
    const code = normaliseCurrencyCode((entry as Record<string, unknown>).code);
    if (code === null) continue;
    const symbol = (entry as Record<string, unknown>).symbol;
    parsed.push({ code, symbol: typeof symbol === 'string' && symbol !== '' ? symbol : code });
  }
  if (parsed.length === 0) return null;

  const fallbackDefault = parsed[0]?.code ?? BASE_CURRENCY;
  const declaredDefault = normaliseCurrencyCode(record.defaultCurrency);
  const defaultCurrency =
    declaredDefault !== null && parsed.some((o) => o.code === declaredDefault)
      ? declaredDefault
      : fallbackDefault;

  return { currencies: parsed, defaultCurrency };
}

/** Is `code` one this deployment offers? */
export function isSupportedCurrency(code: string): boolean {
  return options.some((option) => option.code === code);
}

export function getCurrency(): string {
  return active;
}

export function getCurrencyOptions(): readonly CurrencyOption[] {
  return options;
}

export function currencySnapshot(): CurrencySnapshot {
  return { currency: active, options, source };
}

/** Symbol for a code, from the server's list; falls back to the code. */
export function currencySymbol(code: string = active): string {
  return options.find((option) => option.code === code)?.symbol ?? code;
}

/**
 * Subscribe to currency changes. Called IMMEDIATELY with the current snapshot
 * — same contract as `subscribeI18n`, so a footer has one mounting pattern for
 * both switchers instead of two.
 */
export function subscribeCurrency(fn: (snapshot: CurrencySnapshot) => void): () => void {
  listeners.add(fn);
  fn(currencySnapshot());
  return () => {
    listeners.delete(fn);
  };
}

function emit(): void {
  const snapshot = currencySnapshot();
  for (const listener of [...listeners]) listener(snapshot);
}

/**
 * Resolve and apply the display currency.
 *
 * Order — account > device > server default > EUR. The account wins because a
 * player who set GBP on their phone should see GBP on a borrowed laptop; the
 * device comes next so a signed-out visitor's choice survives a reload. Never
 * rejects: a footer must render whatever the network did.
 */
export async function initCurrency(init: InitCurrencyOptions = {}): Promise<string> {
  applyOptionOverrides(init);

  const config = await deps.loadConfig();
  if (config !== null) options = config.currencies;

  const forced = normaliseCurrencyCode(init.currency);
  if (forced !== null) {
    active = forced;
    source = 'fallback';
    emit();
    return active;
  }

  let resolved: string | null = null;
  let resolvedSource: CurrencySource = 'fallback';

  if (deps.isAuthenticated()) {
    const account = normaliseCurrencyCode(await deps.loadAccountCurrency());
    if (account !== null && isSupportedCurrency(account)) {
      resolved = account;
      resolvedSource = 'account';
    }
  }

  if (resolved === null && deps.storage !== null) {
    let stored: string | null = null;
    try {
      stored = deps.storage.getItem(CURRENCY_STORAGE_KEY);
    } catch {
      stored = null;
    }
    const code = normaliseCurrencyCode(stored);
    if (code !== null && isSupportedCurrency(code)) {
      resolved = code;
      resolvedSource = 'storage';
    }
  }

  if (resolved === null && config !== null) {
    resolved = config.defaultCurrency;
    resolvedSource = 'server-default';
  }

  active = resolved ?? BASE_CURRENCY;
  source = resolved === null ? 'fallback' : resolvedSource;
  emit();
  return active;
}

/**
 * Change the display currency.
 *
 * REJECTS on an unsupported code — a switcher must not pretend to have set a
 * currency this deployment does not enable.
 *
 * A FAILED SAVE DOES NOT REJECT, and that asymmetry is deliberate: it is
 * exactly what `setLocale`/`pushPreference` do in the i18n runtime. The device
 * write happens first and stands, so a signed-in player on a flaky connection
 * keeps seeing the currency they picked and gets it again on the next reload;
 * the account simply has not caught up. Rejecting here would force the caller
 * to choose between reverting a control whose local value is already correct
 * (showing one currency while the app uses another) and swallowing the error.
 * One console warning, once per currency, is the honest middle.
 */
export async function setCurrency(
  code: string,
  { persist = true, remote = true }: SetCurrencyOptions = {},
): Promise<void> {
  const next = normaliseCurrencyCode(code);
  if (next === null || !isSupportedCurrency(next)) {
    throw new Error(`Unsupported currency: ${code}`);
  }
  if (next === active) return;

  if (persist && deps.storage !== null) {
    try {
      deps.storage.setItem(CURRENCY_STORAGE_KEY, next);
    } catch {
      /* private mode — the choice is session-only, which is not worth failing over */
    }
  }

  active = next;
  source = persist ? 'storage' : 'fallback';
  emit();

  if (remote && deps.isAuthenticated()) await pushPreference(next);
}

const warned = new Set<string>();

async function pushPreference(currency: string): Promise<void> {
  try {
    await deps.savePreference(currency);
    source = 'account';
    emit();
  } catch {
    if (warned.has(currency)) return;
    warned.add(currency);
    console.warn(
      `[currency] could not save "${currency}" to the account; it is remembered on this ` +
        `device only.`,
    );
  }
}

/**
 * Adopt the currency stored on the account, AFTER session bootstrap.
 *
 * Deliberately does not echo the value back — reading a preference must never
 * write one, or a stale cache on one device would overwrite a fresh choice
 * made on another. Returns the adopted code, or `null` when there was nothing
 * to adopt. Mirrors `adoptAccountLocale()`.
 */
export async function adoptAccountCurrency(): Promise<string | null> {
  if (!deps.isAuthenticated()) return null;
  const account = normaliseCurrencyCode(await deps.loadAccountCurrency());
  if (account === null || !isSupportedCurrency(account) || account === active) return null;
  active = account;
  source = 'account';
  emit();
  return account;
}

function applyOptionOverrides(init: InitCurrencyOptions): void {
  if (init.storage !== undefined) deps.storage = init.storage;
  if (init.loadConfig !== undefined) deps.loadConfig = init.loadConfig;
  if (init.loadAccountCurrency !== undefined) deps.loadAccountCurrency = init.loadAccountCurrency;
  if (init.savePreference !== undefined) deps.savePreference = init.savePreference;
  if (init.isAuthenticated !== undefined) deps.isAuthenticated = init.isAuthenticated;
}

/* ═══════════════════════════════════════════════ the pack price columns ══ */

/**
 * THE CURRENCIES THE SHARD LADDER IS PRICED IN — a DIFFERENT list from the one
 * above, and the difference is the point of this block.
 *
 * `options` (from `GET /i18n/config`) is the DISPLAY-preference list: which
 * currencies core will render an existing EUR amount in, via
 * `core/exchange-rate`. Core compiles in EUR/GBP/USD (`i18n.constants.ts`), so
 * AUD and PLN are not on it and `setCurrency('PLN')` correctly rejects.
 *
 * This list is the set of currencies a player can be CHARGED in, which is a
 * property of the price table rather than of a rate service: it mirrors
 * `PACK_CURRENCIES` in `backend/src/game/shop/packs.data.ts`, the five columns
 * that table actually authors. Nothing is derived from anything — a rung with
 * no price in a currency is not on sale in that currency, and is never
 * converted into it.
 *
 * It is hard-coded rather than fetched because it is the shape of a frozen
 * table checked at the server's boot, not a deployment setting; and because
 * `GET /api/shop/packs` refuses an unlisted code with a 400, so a client that
 * guessed would get an error page instead of a shelf. The pin is
 * `frontend/test/currency-packs.test.ts`.
 */
export const PACK_CURRENCIES: readonly string[] = Object.freeze([
  'EUR',
  'USD',
  'GBP',
  'AUD',
  'PLN',
]);

/** Is `code` a currency the pack ladder authors a price column for? */
export function isPackCurrency(code: unknown): boolean {
  const normalised = normaliseCurrencyCode(code);
  return normalised !== null && PACK_CURRENCIES.includes(normalised);
}

/**
 * Which price column to open the pack shelf on.
 *
 * The display preference when the ladder has a column for it, `BASE_CURRENCY`
 * otherwise. A player who chose GBP for their invoices sees the GBP ladder
 * without asking twice; a player whose deployment defaults them to a currency
 * the ladder is not priced in sees EUR and can switch — rather than seeing an
 * empty shelf, or a EUR figure wearing a złoty symbol, which is what a
 * fallback that kept the code but not the column would produce.
 */
export function resolvePackCurrency(preferred: string = active): string {
  return isPackCurrency(preferred) ? (normaliseCurrencyCode(preferred) as string) : BASE_CURRENCY;
}

/** Test seam: back to a pristine module. */
export function resetCurrencyForTests(): void {
  deps = freshDeps();
  configCache = null;
  options = FALLBACK_CURRENCIES.map((code) => ({ code, symbol: code }));
  active = BASE_CURRENCY;
  source = 'fallback';
  listeners.clear();
  warned.clear();
}
